<script setup lang="ts">
	import { format, formatDistanceToNow } from 'date-fns'
	import { es } from 'date-fns/locale'
	import { computed, reactive, ref, watch } from 'vue'

	import { useAuth } from '@/composables/useAuth'

	import useCompany from '@/modules/companies/composables/useCompany'
	import useExportCompanyReportHotels from '@/modules/companies/composables/useExportCompanyReportHotels'
	import type { Company } from '@/modules/companies/interfaces/company'
	import { ReportViewType } from '@/modules/reports/interfaces/reportData'

	import HotelReportAccessNotice from '../components/HotelReportAccessNotice.vue'
	import HotelReportFiltres from '../components/HotelReportFiltres.vue'
	import HotelReportSources from '../components/HotelReportSources.vue'
	import ReportsCharts from '../components/ReportsCharts.vue'
	import ReportsTable from '../components/ReportsTable.vue'
	import { useHotelReportAccess } from '../composables/useHotelReportAccess'

	export interface Filters {
		dateRange: string[] | null
		creationDateRange: string[] | null | undefined
		statusFilter: string
		groupByFilter: string
		datePreset: string
		creationDatePreset: string
		searchTravelerId: number | null
		searchHotelId: number | null
		searchTravelerName: string | null
		searchHotelName: string | null
		createdAt: string[] | null
		orderBy: string
		orderDirection: 'asc' | 'desc'
	}

	const viewType = ref<ReportViewType>('table')

	const showFilters = ref(false)

	const reportsChartsRef = ref<InstanceType<typeof ReportsCharts> | null>(null)

	const setViewType = (type: ReportViewType) => {
		viewType.value = type
	}

	const { user } = useAuth()

	const { company } = useCompany(
		{
			companyId: user.value?.companyId,
		},
		{
			immediate: !user.value?.internal,
		}
	)

	const selectedCompany = ref<Company | null>(company.value ?? null)
	const accessCompanyId = computed(() => selectedCompany.value?.id)
	const { statuses, allowed, checking, message, refresh } =
		useHotelReportAccess(accessCompanyId, 'local')

	watch(
		() => company.value,
		(newCompany) => {
			if (newCompany) {
				selectedCompany.value = newCompany
			}
		},
		{ immediate: true }
	)

	const filters = reactive<Filters>({
		dateRange: null as string[] | null,
		creationDateRange: null as string[] | null,
		statusFilter: 'all',
		groupByFilter: 'month',
		datePreset: '1month',
		creationDatePreset: '',
		searchTravelerId: null,
		searchHotelId: null,
		searchTravelerName: null,
		searchHotelName: null,
		createdAt: null as string[] | null,
		orderBy: 'created_at',
		orderDirection: 'desc',
	})

	// Actualizar filtres cuando se reciben cambios del componente
	const handleFiltersUpdate = (newFilters: Filters) => {
		Object.assign(filters, newFilters)
	}

	// Contador de filtros activos
	const activeFiltersCount = computed(() => {
		let count = 0
		if (filters.searchTravelerId) count++
		if (filters.searchHotelId) count++
		if (filters.statusFilter !== 'all') count++
		if (filters.dateRange && filters.dateRange.length > 0) count++
		if (
			(filters.creationDateRange && filters.creationDateRange.length > 0) ||
			(filters.creationDatePreset && filters.creationDatePreset !== '')
		)
			count++
		if (filters.orderBy || filters.orderDirection) count++
		return count
	})

	const exporting = ref(false)
	const exportingType = ref<'excel' | 'pdf' | null>(null)

	const { exportReport } = useExportCompanyReportHotels()

	const handleExportExcel = async () => {
		if (!(await refresh()) || !selectedCompany.value) return null

		exporting.value = true
		exportingType.value = 'excel'
		try {
			await exportReport({
				companyId: selectedCompany.value?.id,
				filters: {
					dates: filters.dateRange,
					creationDates: filters.creationDateRange,
					travelerId: filters.searchTravelerId ?? undefined,
					hotelId: filters.searchHotelId ?? undefined,
					createdAt: filters.createdAt,
					orderBy: filters.orderBy,
					orderDirection: filters.orderDirection,
				},
				format: 'csv',
			})
		} finally {
			exporting.value = false
			exportingType.value = null
		}
	}

	const handleExportPDF = async () => {
		if (!(await refresh()) || !selectedCompany.value) return null

		exporting.value = true
		exportingType.value = 'pdf'
		try {
			await exportReport({
				companyId: selectedCompany.value.id,
				filters: {
					dates: filters.dateRange,
					creationDates: filters.creationDateRange,
					travelerId: filters.searchTravelerId || undefined,
					hotelId: filters.searchHotelId || undefined,
					createdAt: filters.createdAt,
					orderBy: filters.orderBy,
					orderDirection: filters.orderDirection,
				},
				format: 'pdf',
			})
		} finally {
			exporting.value = false
			exportingType.value = null
		}
	}

	const handleExportChartsPDF = async () => {
		if ((await refresh()) && reportsChartsRef.value) {
			await reportsChartsRef.value.exportPdf()
		}
	}

	const isExportingCharts = computed(() => {
		return reportsChartsRef.value?.isExporting ?? false
	})

	const isLoadingChartsData = computed(() => {
		return reportsChartsRef.value?.isLoadingData ?? false
	})

	// Clases computadas para botones de toggle
	const getViewButtonClass = (type: ReportViewType) => {
		return computed(() => {
			const baseClasses =
				'px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium transition-all focus:outline-none'
			const isActive = viewType.value === type

			if (type === 'table') {
				return `${baseClasses} rounded-l-md ${
					isActive ?
						'bg-blue-ef-500 text-white shadow-sm'
					:	'bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-ef-500'
				}`
			} else {
				return `${baseClasses} rounded-r-md ${
					isActive ?
						'bg-blue-ef-500 text-white shadow-sm'
					:	'bg-white text-gray-700 hover:bg-gray-50 hover:text-blue-ef-500'
				}`
			}
		})
	}

	const getStatusLabel = (status: string): string => {
		const statusLabels: Record<string, string> = {
			pending: 'Pendiente',
			confirmed: 'Confirmada',
			cancelled: 'Cancelada',
			completed: 'Completada',
			in_progress: 'En progreso',
		}
		return statusLabels[status] || status
	}

	const formatDateRange = (dateRange: string[] | null | undefined): string => {
		if (!dateRange || dateRange.length === 0) return ''

		const startDate = new Date(dateRange[0])
		const endDate = new Date(dateRange[1])
		const now = new Date()

		const todayStart = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate()
		)
		const startDateNormalized = new Date(
			startDate.getFullYear(),
			startDate.getMonth(),
			startDate.getDate()
		)
		const endDateNormalized = new Date(
			endDate.getFullYear(),
			endDate.getMonth(),
			endDate.getDate()
		)

		const isFutureRange =
			startDateNormalized.getTime() === todayStart.getTime() &&
			endDateNormalized.getTime() > todayStart.getTime()

		if (isFutureRange) {
			const distance = formatDistanceToNow(endDate, {
				locale: es,
				addSuffix: false,
			})
			return `Próximos ${distance}`
		}

		const isPastRange =
			endDateNormalized.getTime() === todayStart.getTime() &&
			startDateNormalized.getTime() < todayStart.getTime()

		if (isPastRange) {
			const distance = formatDistanceToNow(startDate, {
				locale: es,
				addSuffix: false,
			})
			return `Últimos ${distance}`
		}

		const start = format(startDate, 'dd/MM/yyyy', { locale: es })
		const end = format(endDate, 'dd/MM/yyyy', { locale: es })
		return `${start} - ${end}`
	}

	const getOrderLabel = (orderBy: string, direction: string): string => {
		const orderLabels: Record<string, string> = {
			created_at: 'Fecha de creación',
			start_date: 'Fecha de inicio',
			end_date: 'Fecha de fin',
			total: 'Total',
			traveler_name: 'Nombre del viajero',
			suppliable_name: 'Nombre del hotel',
			check_in: 'Check-in',
			check_out: 'Check-out',
			total_cost: 'Costo total',
			hotel_name: 'Hotel',
		}
		const directionLabel = direction === 'asc' ? 'Ascendente' : 'Descendente'
		return `${orderLabels[orderBy] || orderBy} (${directionLabel})`
	}

	const presetLabels: Record<string, string> = {
		'1monthNext': 'Próximo mes',
		'3monthNext': 'Próximos 3 meses',
		'6monthNext': 'Próximos 6 meses',
		year: 'Este año',
		'1month': 'Último mes',
		'3months': 'Últimos 3 meses',
		'6months': 'Últimos 6 meses',
		lastYear: 'Último año',
	}

	const getDateRangeLabel = (): string => {
		if (filters.datePreset === 'custom' && filters.dateRange) {
			return formatDateRange(filters.dateRange)
		}

		return presetLabels[filters.datePreset] || 'Período de tiempo'
	}

	const getCreationDateLabel = (): string => {
		if (filters.creationDatePreset === 'custom' && filters.creationDateRange) {
			return formatDateRange(filters.creationDateRange)
		}

		return presetLabels[filters.creationDatePreset] || 'Fecha de creación'
	}
</script>

<template>
	<div class="min-h-screen bg-slate-50">
		<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
			<div class="mb-6 space-y-3">
				<HotelReportSources :access="statuses" />
				<HotelReportAccessNotice
					v-if="!allowed"
					:message="message"
					:checking="checking"
					@retry="refresh"
				/>
				<p
					class="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-950"
				>
					Fuente: Booking nuevo. Este reporte incluye únicamente las reservas
					registradas en BookingV2 dentro de tu alcance de acceso.
				</p>
			</div>

			<!-- Page header -->
			<div
				class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
			>
				<div>
					<h1 class="text-2xl font-bold text-gray-900 tracking-tight">
						Reportes de Hotelería
					</h1>
					<p class="text-sm text-gray-500 mt-1">
						Análisis y métricas de reservas hoteleras
					</p>
				</div>

				<!-- Action bar: export buttons + filter button -->
				<div class="flex items-center gap-2 flex-wrap">
					<!-- Export buttons for table view -->
					<template v-if="allowed && viewType === 'table'">
						<button
							type="button"
							:disabled="exporting"
							class="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
							@click="handleExportExcel"
						>
							<span class="material-symbols-outlined text-base">
								{{ exportingType === 'excel' ? 'hourglass_empty' : 'grid_on' }}
							</span>
							{{ exportingType === 'excel' ? 'Exportando...' : 'Excel' }}
						</button>
						<button
							type="button"
							:disabled="exporting"
							class="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
							@click="handleExportPDF"
						>
							<span class="material-symbols-outlined text-base">
								{{
									exportingType === 'pdf' ? 'hourglass_empty' : 'picture_as_pdf'
								}}
							</span>
							{{ exportingType === 'pdf' ? 'Exportando...' : 'PDF' }}
						</button>
					</template>

					<!-- Export button for charts view -->
					<template v-else-if="allowed && viewType === 'charts'">
						<button
							type="button"
							:disabled="isExportingCharts || isLoadingChartsData"
							class="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
							@click="handleExportChartsPDF"
						>
							<span
								class="material-symbols-outlined text-base"
								:class="{
									'animate-spin': isExportingCharts || isLoadingChartsData,
								}"
							>
								{{
									isExportingCharts || isLoadingChartsData ? 'progress_activity'
									:	'picture_as_pdf'
								}}
							</span>
							{{
								isExportingCharts ? 'Generando PDF...'
								: isLoadingChartsData ? 'Cargando...'
								: 'PDF'
							}}
						</button>
					</template>

					<!-- Filter button -->
					<button
						type="button"
						class="inline-flex items-center gap-1.5 bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200"
						@click="showFilters = true"
					>
						<span class="material-symbols-outlined text-base">filter_list</span>
						Filtros
						<span
							v-if="activeFiltersCount > 0"
							class="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full"
						>
							{{ activeFiltersCount }}
						</span>
					</button>
				</div>
			</div>

			<template v-if="allowed">
				<!-- View toggle: modern pills -->
				<div
					class="flex items-center gap-1 bg-gray-100/70 rounded-xl p-1 w-fit mb-6"
				>
					<button
						:class="[
							viewType === 'table' ?
								'bg-white text-gray-900 shadow-sm'
							:	'text-gray-500 hover:text-gray-700',
							'px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2',
						]"
						@click="setViewType('table')"
					>
						<span class="material-symbols-outlined text-base">table_chart</span>
						Tabla
					</button>
					<button
						:class="[
							viewType === 'charts' ?
								'bg-white text-gray-900 shadow-sm'
							:	'text-gray-500 hover:text-gray-700',
							'px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2',
						]"
						@click="setViewType('charts')"
					>
						<span class="material-symbols-outlined text-base">bar_chart</span>
						Gráficos
					</button>
				</div>

				<!-- Active filter chips -->
				<div
					v-if="activeFiltersCount > 0"
					class="flex flex-wrap gap-2 mb-6"
				>
					<!-- Viajero -->
					<span
						v-if="filters.searchTravelerId"
						class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm text-gray-700"
					>
						<span class="material-symbols-outlined text-sm">person</span>
						<span class="truncate max-w-[120px] sm:max-w-none">{{
							filters.searchTravelerName || 'Viajero'
						}}</span>
						<button
							@click="
								() => {
									filters.searchTravelerId = null
									filters.searchTravelerName = null
								}
							"
							class="ml-0.5 hover:bg-gray-100 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>

					<!-- Hotel -->
					<span
						v-if="filters.searchHotelId"
						class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm text-gray-700"
					>
						<span class="material-symbols-outlined text-sm">hotel</span>
						<span class="truncate max-w-[120px] sm:max-w-none">{{
							filters.searchHotelName || 'Hotel'
						}}</span>
						<button
							@click="
								() => {
									filters.searchHotelId = null
									filters.searchHotelName = null
								}
							"
							class="ml-0.5 hover:bg-gray-100 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>

					<!-- Estado -->
					<span
						v-if="filters.statusFilter !== 'all'"
						class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm text-gray-700"
					>
						<span class="material-symbols-outlined text-sm">check_circle</span>
						<span class="truncate"
							>Estatus: {{ getStatusLabel(filters.statusFilter) }}</span
						>
						<button
							@click="filters.statusFilter = 'all'"
							class="ml-0.5 hover:bg-gray-100 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>

					<!-- Rango de fechas -->
					<span
						v-if="filters.dateRange && filters.dateRange.length > 0"
						class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm text-gray-700"
					>
						<span class="material-symbols-outlined text-sm"
							>calendar_today</span
						>
						<span class="truncate max-w-[150px] sm:max-w-none"
							>Reserva: {{ getDateRangeLabel() }}</span
						>
						<button
							@click="filters.dateRange = null"
							v-if="filters.creationDateRange && filters.creationDateRange"
							class="ml-0.5 hover:bg-gray-100 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>

					<!-- Rango de fechas de creación -->
					<span
						v-if="
							(filters.creationDateRange &&
								filters.creationDateRange.length > 0) ||
							(filters.creationDatePreset && filters.creationDatePreset !== '')
						"
						class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm text-gray-700"
					>
						<span class="material-symbols-outlined text-sm">event</span>
						<span class="truncate max-w-[150px] sm:max-w-none"
							>Creación: {{ getCreationDateLabel() }}</span
						>
						<button
							@click="
								() => {
									filters.creationDateRange = null
									filters.creationDatePreset = ''
								}
							"
							v-if="filters.dateRange && filters.dateRange"
							class="ml-0.5 hover:bg-gray-100 rounded-full p-0.5"
						>
							<span class="material-symbols-outlined text-sm cursor-pointer"
								>close</span
							>
						</button>
					</span>

					<!-- Ordenamiento -->
					<span
						class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm text-gray-700"
					>
						<span class="material-symbols-outlined text-sm">sort</span>
						<span class="hidden sm:inline">Ordenar:</span>
						<span class="truncate max-w-[120px] sm:max-w-none">
							{{ getOrderLabel(filters.orderBy, filters.orderDirection) }}
						</span>
						<button
							@click="
								() => {
									filters.orderBy = 'created_at'
									filters.orderDirection = 'desc'
								}
							"
							class="ml-0.5 hover:bg-gray-100 rounded-full p-0.5"
						>
							<span
								class="material-symbols-outlined text-sm cursor-pointer"
								v-if="filters.orderBy !== 'created_at'"
								>close</span
							>
						</button>
					</span>
				</div>

				<!-- Content: no wrapper box, let inner components breathe -->
				<ReportsTable
					v-if="viewType === 'table'"
					:company-id="selectedCompany?.id"
					:date-range="filters.dateRange"
					:status-filter="filters.statusFilter"
					:creation-date-range="filters.creationDateRange"
					:date-preset="filters.datePreset"
					:creation-date-preset="filters.creationDatePreset"
					:group-by-filter="filters.groupByFilter"
					:search-traveler-id="filters.searchTravelerId"
					:search-hotel-id="filters.searchHotelId"
					:created-at="filters.createdAt"
					:order-by="filters.orderBy"
					:order-direction="filters.orderDirection"
				/>
				<ReportsCharts
					v-else
					ref="reportsChartsRef"
					:company-id="selectedCompany?.id"
					:company-logo="selectedCompany?.logo"
					:company-name="selectedCompany?.name"
					:date-range="filters.dateRange"
					:creation-date-range="filters.creationDateRange"
					:date-preset="filters.datePreset"
					:creation-date-preset="filters.creationDatePreset"
					:status-filter="filters.statusFilter"
					:group-by-filter="filters.groupByFilter"
					:search-traveler-id="filters.searchTravelerId"
					:search-hotel-id="filters.searchHotelId"
					:created-at="filters.createdAt"
					:order-by="filters.orderBy"
					:order-direction="filters.orderDirection"
				/>
			</template>
		</div>
	</div>

	<HotelReportFiltres
		:filtres="filters"
		:selected-company="selectedCompany"
		:show-filters="showFilters"
		@update:show-filters="showFilters = $event"
		@update:filtres="handleFiltersUpdate"
		@update:selected-company="selectedCompany = $event"
	/>
</template>

<style scoped>
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.2s ease;
	}

	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}
</style>
